// ============================================================
// ExposuresSection - what a target is EXPOSED to, once (v8.351.0).
//
// The Exposures counterpart of ConditionsSection, under it in both
// panels. An exposure is an `Ex:` code in the same `conditions` array
// (ADR 2026-09-12: a code, not a field), so this component FILTERS the
// list and hands back ORIGINAL indices - the writers stay with the
// panels, exactly as the conditions block leaves them there.
//
// THE BLOCK SHOWS ONLY WHEN SOMEONE IS EXPOSED (v8.356.2). The verb -
// `ExposeButton` - lives in the Conditions header, so an empty block
// costs the Status tab nothing: an extra `auto` row under the map took
// a badge-heavy sheet's rail from eight rows to seven and spilled its
// columns sideways (status-tab.spec.js:200).
// ============================================================
import { useState } from 'react';
import clsx from '../utils/clsx';
import AddExposureModal, { exposureColour } from './AddExposureModal';
import { describeExposure, isExposureCode } from '../engine/exposures';
import type { Target } from '../types';
import { ConditionChip } from './ConditionChip';
import BadgeRow from './BadgeRow';
import common from '../styles/common.module.css';
import styles from '../styles/ConditionsSection.module.css';

export interface ExposuresSectionProps {
  target: Target;
  /** The index in `target.conditions` - the whole list, not the exposures alone. */
  onRemove: (index: number) => void;
  onClear: () => void;
  locked?: boolean;
  className?: string;
}

/** The exposures in a conditions list, each with its original index. */
export function exposureEntries(conditions: readonly string[] | undefined): { index: number; code: string }[] {
  return (conditions ?? []).flatMap((code, index) => (isExposureCode(code) ? [{ index, code }] : []));
}

/** The verb: `+ Expose` and the picker it opens. Lives in the Conditions header. */
export function ExposeButton({
  onAdd,
  locked = false,
  teams = [],
}: {
  onAdd: (code: string) => void;
  locked?: boolean;
  teams?: { tag: string; label?: string }[];
}) {
  const [showAdd, setShowAdd] = useState(false);
  return (
    <>
      <button
        className={common.btnSmall}
        onClick={() => setShowAdd(true)}
        disabled={locked}
        data-testid="add-exposure-btn"
      >
        + Expose
      </button>
      {showAdd && <AddExposureModal onAdd={onAdd} onClose={() => setShowAdd(false)} teams={teams} />}
    </>
  );
}

export default function ExposuresSection({
  target,
  onRemove,
  onClear,
  locked = false,
  className,
}: ExposuresSectionProps) {
  const entries = exposureEntries(target.conditions);
  // What the SHEET says they are (v8.355.0): Fear, the Black Carapace, a
  // holo-field - read-only here, edited in the character's Exposures tab.
  const innate = target.exposures ?? [];
  if (entries.length === 0 && innate.length === 0) return null;

  return (
    <div
      className={clsx(styles.wrap, locked && common.ariaDisabled, className)}
      aria-disabled={locked || undefined}
      data-testid="exposures-section"
    >
      <div className={styles.header}>
        <span className={styles.heading}>Exposures ({entries.length + innate.length})</span>
        <span className={styles.actions} data-ghost-hide="true">
          {entries.length > 0 && (
            <button className={clsx(common.btnSmall, styles.clear)} onClick={onClear} disabled={locked}>
              Clear All
            </button>
          )}
        </span>
      </div>

      {/* One line: past it, "+N" opens the rest - so the plate above keeps its height (2026-10-24). */}
      <div className={styles.list} data-testid="exposures-list">
        <BadgeRow
          label="Exposures"
          full={<ExposureChips innate={innate} entries={entries} locked={locked} onRemove={onRemove} />}
        >
          <ExposureChips innate={innate} entries={entries} locked={locked} onRemove={onRemove} ids />
        </BadgeRow>
      </div>
    </div>
  );
}

/** The exposure chips: the sheet's innate ones (read-only), then the worn ones. `ids` on the line's copy only. */
function ExposureChips({
  innate,
  entries,
  locked,
  onRemove,
  ids = false,
}: {
  innate: readonly string[];
  entries: readonly { index: number; code: string }[];
  locked: boolean;
  onRemove: (index: number) => void;
  ids?: boolean;
}) {
  return (
    <>
      {innate.map((code, i) => (
        <ConditionChip
          key={`innate-${code}-${i}`}
          colours={exposureColour(code)}
          label={describeExposure(code) ?? code}
          onRemove={null}
          opacity={0.85}
          title="Innate - from the sheet"
          testId={ids ? `exposure-innate-${i}` : undefined}
        >
          {describeExposure(code) ?? code} · innate
        </ConditionChip>
      ))}
      {entries.map(({ index, code }) => (
        <ConditionChip
          key={`${code}-${index}`}
          colours={exposureColour(code)}
          label={describeExposure(code) ?? code}
          onRemove={locked ? null : () => onRemove(index)}
          testId={ids ? `exposure-chip-${index}` : undefined}
        />
      ))}
    </>
  );
}
